/**
 * WildsModule — dangerous creatures, ground combat, and the mission terminal.
 *
 *   creatures  renders the server-authoritative `creature` rows (Higgsfield
 *              GLBs, lerped between sim ticks, HP bars, death = gone until
 *              respawn) and drives `wildsTick` from the FIXED loop (never
 *              setTimeout — the timer-throttling law).
 *   combat     F strikes the nearest living creature in reach; your vitals
 *              render as a HUD bar; a maul flashes the screen; running dry
 *              means waking up at the nearest settlement's medbay.
 *   terminal   Z at a city/town work terminal opens the board — six postings
 *              derived from the same pure lib the server checks (culls aimed
 *              at the dens actually menacing the town, deliveries to named
 *              settlements, wilderness surveys) — accept / track / complete /
 *              abandon, paid in SOV + settlement standing.
 *
 * Debug surface: `window.__wilds` — telemetry always; actions dev-gated.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Identity } from 'spacetimedb';
import type { App, IModule } from 'enginex';
import { disposeLater } from '../lib/gpu_dispose.js';
import { Logger } from 'enginex/core/Logger.js';
import { applyDissolve } from 'enginex/vfx/DissolveModifier.js';
import { activePreset } from '../lib/quality_tiers.js';
import { dissolveProgress, DISSOLVE_S, stage3Stats } from '../lib/stage3_fx.js';
import { warmThenShow } from '../lib/pipeline_warm.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { pickTargetStable, REACH_PAD_M, HEAVY_DMG_MIN } from '../lib/combat_core.js';
import { legalPlayerTargets, myActor, playerName } from '../lib/pvp_context.js';
import { feel, frozen, hitStop, cameraImpulse, addTrauma, recoilFor, windupPose, KICK_TAKEN_PITCH, KICK_DEALT_PITCH, TRAUMA_TAKEN, TRAUMA_DEALT, KNOCKBACK_M, WINDUP_LEAN_RAD } from '../lib/combat_feel.js';
import { makeWildGait, stepWildGait, wildGaitFor, makeGlide, glideRow, stepGlide, turnToward, headingOf, type WildGaitState, type WildGaitSpec, type WildGlide } from '../lib/wild_gait.js';
import { wildParts, type WildParts } from '../lib/wild_models.js';
import { windups, telegraphBegin, telegraphEnd, lungeAt, pruneTelegraphs, activeWindupCount, markHurt, markStruckByPlayer } from '../lib/combat_telegraph.js';
import { startRttSampler, nextNonce, rttStats } from '../lib/rtt.js';
import { ProjectileField } from '../lib/projectiles.js';
import { densFor } from '../lib/wilds.js';
import { chargeAfter, feedReady } from '../lib/combat_core.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { sfx, sfxAt, sfxLater, sfxLoop, inverseDistanceGain } from '../lib/sfx.js';
import { WEAPONS, FISTS, type WeaponSpec } from '../lib/weapon_catalog.js';
import { terrainSeat } from '../lib/terrain_seat.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import type { Civilization } from '../lib/biome_settlements.js';
import { civOfSettlementRowC, civRowGeneration } from '../lib/civ_row.js';
import { missionsFor, missionEpoch, SPECIES, TERMINAL_RANGE_M, STRIKE_RANGE_M, PLAYER_MAX_HP, type MissionSpec } from '../lib/wilds.js';
import { prefabAssetId } from '../lib/player_models.js';
import { groundTargetList } from '../lib/ground_targets.js';
import { groundYIn } from '../lib/grounding.js';
import { nameFromTables } from '../lib/versona_names.js';
import { impactDecals, impactDecalsIfAny, groundNormal, rayGroundHit, type DecalKind } from '../lib/impact_decals.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { AssetModule } from './AssetModule.js';
import { waterStillAt, waterLiveAt, stampWater, splashWater, waterFx } from '../lib/water_fx.js';
import { firstWaterCrossing, waterShotFor, WATER_SHOT_REF_M, WATER_SHOT_MAX_M, type WaterCrossing, type WaterShotKind } from '../lib/water_interact.js';
import { creatureStamp } from '../lib/water_physics.js';
import { incidence, decalStretch, bodyMuzzle } from '../lib/blaster.js';
import { litSmokePuff, initLitSmoke } from '../lib/lit_smoke.js';
import { VISUAL_RANGE_M } from '../lib/projectiles.js';
import { impactProfile, scaleProfile } from '../lib/surface_response.js';
import { reportDamageHit, damageDecalLifeS } from '../lib/damage_runtime.js'; // shader gaps R2: persistent material damage
import { ImpactLightPool } from '../lib/impact_lights.js';
import { clientSight, ensureIndoorClient, indoorCombatDiag, remoteSeat } from '../lib/indoor_combat_client.js'; // slice K: the client mirrors the judge
import { enterables } from '../lib/building_interiors.js';
import { biomeAt } from '@shared/terrain.js';

const rowFirstCiv = (siteSeed: number, siteId?: number): Civilization =>
  siteId === undefined ? civilizationOf(siteSeed) : civOfSettlementRowC(BigInt(siteId + 1), siteSeed);

interface Ts { microsSinceUnixEpoch: bigint }
interface CreatureRow { creatureId: bigint; denKey: string; species: string; x: number; z: number; hp: number; state: string; targetKind: string; targetPlayer: { isEqual(o: unknown): boolean }; updatedAt: Ts }
/** A shootable thing on the ground — every kind the ONE attack path can pick. */
interface StrikeCand { kind: 'creature' | 'versona' | 'hunter' | 'player'; id: string; label: string; x: number; z: number; y: number | null; fire: () => Promise<void>; key?: string; legal?: boolean }
interface VersonaHotRow { versonaId: bigint; worldId: bigint; curX: number; curZ: number; state: string }
/** COMBAT WAVE: an npc_strike row (the telegraph) as the client reads it. */
interface NpcStrikeRow { attackerKey: string; targetKind: string; targetPlayer: { isEqual(o: unknown): boolean }; targetVersona: bigint; windupMs: number; x: number; z: number }
interface WeaponStateRow { identity: { isEqual(o: unknown): boolean }; itemKey: string; rounds: number; charge: number; chargeAt: { microsSinceUnixEpoch: bigint }; reloadUntil: { microsSinceUnixEpoch: bigint } }
/** The server's creature ATTACK_RANGE_M (wilds.ts) — the ring fills toward it. */
const ATTACK_REACH_M = 3.4;
interface VitalsRow { identity: { isEqual(o: unknown): boolean }; hp: number; maxHp: number }
interface HoldRow { missionHoldId: bigint; identity: { isEqual(o: unknown): boolean }; sid: bigint; slot: number; epoch: number; kind: string; species: string; targetSiteId: bigint; wayX: number; wayZ: number; needed: number; progress: number; rewardSov: bigint; standing: number; state: string }

const SPECIES_LEN: Record<string, number> = {
  ridge_stalker: 2.6, spore_drone: 1.7, duneback_brute: 3.8,
  mire_lurker: 2.4, crag_wyrm: 4.4, glass_shrike: 1.9,
};
/** Nameplate vocabulary: display name + a descriptive title (ground-combat
 *  wave — every beast introduces itself above its health bar). */
const SPECIES_PLATE: Record<string, { title: string; sub: string }> = {
  ridge_stalker: { title: 'Ridge Stalker', sub: 'swift pack hunter' },
  spore_drone: { title: 'Spore Drone', sub: 'drifting fungal swarm' },
  duneback_brute: { title: 'Duneback Brute', sub: 'armored heavyweight' },
  mire_lurker: { title: 'Mire Lurker', sub: 'wetland ambusher' },
  crag_wyrm: { title: 'Crag Wyrm', sub: 'stone-scaled apex' },
  glass_shrike: { title: 'Glass Shrike', sub: 'crystalline raptor' },
};
const TICK_EVERY = 120;      // drive wildsTick every ~2 s of fixed time
const esc = (v: string): string => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const pretty = (k: string): string => k.replace(/_/g, ' ');

interface Beast {
  root: THREE.Group;
  bar: THREE.Sprite;
  barCtx: CanvasRenderingContext2D;
  barTex: THREE.CanvasTexture;
  lastHp: number;
  lastState: string; // aggro-edge detector ('' until first seen)
  targetPos: THREE.Vector3;
  /** Row position the cached TARGET ground Y was sampled at (rows move every
   *  sim tick, ~2 s). The per-frame re-seat at the glide position rides the
   *  meshHeight chunk-grid fast path (4 array reads per sample). */
  seatX: number;
  seatZ: number;
  /** Species-length-scaled seat radius (0.45 × body length, ≥ 0.6 m). */
  footR: number;
  /** Interactive water: where the beast last rang the surface (stride-spaced stamps). */
  wadeX?: number;
  wadeZ?: number;
  /** COMBAT WAVE: the telegraph ring drawn under a winding-up beast. */
  ring: THREE.Mesh | null;
  /** THE GAIT (physics follow-up item 3, 2026-10-05): the drawn body walks the rows at their measured pace (lib/
   *  wild_gait.ts) and the shader bends it (lib/wild_models.ts) — never the old surge-and-coast lerp of a statue. */
  glide: WildGlide;
  gait: WildGaitState;
  spec: WildGaitSpec;
  /** The species' server speed (m/s) — bounds the measured pace. */
  speciesSpeed: number;
  /** The per-beast shader state, read PER DRAW by the species material (`mesh.userData.wildA / wildB`). */
  wildA: THREE.Vector4;
  wildB: THREE.Vector4;
  /** The drawn heading (rad) — turns at a rate, never snaps. */
  yaw: number;
  /** The knockback, a decaying visual offset over the glide (a blow never moves the glide itself). */
  kickX: number;
  kickZ: number;
}

export class WildsModule implements IModule {
  readonly name = 'Wilds';
  private app: App | null = null;
  private assets: AssetModule | null = null;
  private root: THREE.Group | null = null;
  private beasts = new Map<string, Beast>();
  /** Creatures burning away (shader wave stage 3): the root stays in the scene
   *  for DISSOLVE_S with the engine dissolve on cloned materials, then goes. */
  private dying: Array<{ root: THREE.Group; mats: THREE.Material[]; handles: Array<{ progress: { value: number } }>; t: number }> = [];
  /** COMBAT WAVE: shot index for the deterministic recoil pattern; resets after idle. */
  private shotIndex = 0;
  private lastShotMs = 0;
  private ringGeo: THREE.RingGeometry | null = null;
  private ringMat: THREE.MeshBasicMaterial | null = null;
  private combatSubbed = false;
  /** The guns' projectiles (combat wave 2026-09-05): pooled, purely visual. */
  private projectiles: ProjectileField | null = null;
  private turretBound = false;
  /** Short-lived lights for impacts — bloom spreads pixels, it does not light. */
  private impactLights: ImpactLightPool | null = null;
  /** Presentation queued onto the bolt's arrival (the flinch waits for the bolt). */
  private onBoltArrived: (() => void) | null = null;
  private readonly _dbSize = new THREE.Vector2();
  private readonly _muzzle = new THREE.Vector3();
  private readonly _mark = new THREE.Vector3();
  private lastWhiffSeen = 0;
  private lastWhiffFlash = 0;
  /** Probe diag: keydowns that reached this listener / F presses that passed its gates. */
  private readonly keyDiag = { seen: 0, f: 0, bus: 0 };
  /** Probe diag: the last strike this client sent (and whether the cone pick found it). */
  private lastStrike: { kind: StrikeCand['kind'] | 'none'; id: string; creatureId: string; picked: boolean; at: number; d: number; voice: string; projectile: boolean; surface?: string | null; cover?: boolean } | null = null;
  /** One grudge toast per versona per 15 s (a burst of shots is one announcement). */
  private readonly versonaToastAt = new Map<string, number>();
  private readonly _aimDir = new THREE.Vector3();
  private readonly _hitN = new THREE.Vector3();
  private readonly _hitP = new THREE.Vector3();
  private phys: PhysicsModule | null = null;
  /** THE GAIT: one bent body per species (geometry + material — ours; the master is the asset cache's). */
  private parts = new Map<string, WildParts | null>();
  private partsWarmed = new Set<string>();
  private masters = new Map<string, THREE.Object3D | null>();
  private readonly masterInflight = new Map<string, Promise<THREE.Object3D | null>>();
  private readonly masterIds = new Map<string, bigint>();
  private tickN = 0;
  private subbed = false;
  private seed = '';
  private civGen = -1;
  private sites: SettlementSite[] = [];
  private names = new Map<number, string>();
  private win: UiWindow | null = null;
  private winBody: HTMLDivElement | null = null;
  private winSid = -1;
  private hud: HTMLDivElement | null = null;
  private prompt: HTMLDivElement | null = null;
  private flash: HTMLDivElement | null = null;
  private lastHp = PLAYER_MAX_HP;
  private lastError = '';

  init(app: App): void {
    this.app = app;
    this.assets = app.getModule('AssetModule') as AssetModule | null;
    this.root = new THREE.Group();
    this.root.name = 'wilds';
    app.world.scene.add(this.root);
    this.projectiles = new ProjectileField(app.world.scene);
    // CITY TURRETS (2026-09-23): the server writes a city_turret_shot row per blow; draw the bolt from the
    // turret's barrel to the target (absolute coordinates — the projectile field's law).
    bus.on('atlas:account-ready', () => {
      const conn = atlas.conn as unknown as { db: { cityTurretShot?: { onInsert(cb: (ctx: unknown, row: { turretStructureId: bigint; targetX: number; targetZ: number }) => void): void }; structure: { structureId: { find(id: bigint): { posX: number; posZ: number; worldId: bigint } | null | undefined } } } } | null;
      const tbl = conn?.db.cityTurretShot;
      if (!tbl || this.turretBound) return;
      this.turretBound = true;
      tbl.onInsert((_c, row) => {
        const st = conn!.db.structure.structureId.find(row.turretStructureId);
        if (!st || !this.projectiles || !atlas.conn) return;
        const y0 = groundYIn(atlas.conn, atlas.currentWorldId, st.posX, st.posZ) + 2.2;
        const y1 = groundYIn(atlas.conn, atlas.currentWorldId, row.targetX, row.targetZ) + 1.0;
        this.projectiles.fire({ kind: 'bolt', speedMps: 90, color: 0x5fd8ff, size: 0.16 },
          new THREE.Vector3(st.posX, y0, st.posZ), new THREE.Vector3(row.targetX, y1, row.targetZ), null, true);
      });
    });
    initLitSmoke(app);
    // The impact-light budget rides the tier: lights are the expensive half of
    // an impact (every receiver in range re-shades), so the cheap rungs get two
    // and the rich ones eight.
    // Kept SMALL. Pool membership is fixed for the session (the light-set
    // identity law), so every slot is a permanent point light in the forward
    // light loop for every lit fragment — on top of StreetLampsModule's ten.
    // Four is enough for a firefight; the cheap tiers take two.
    const lp = activePreset();
    this.impactLights = new ImpactLightPool(app.world.scene, lp.shadowsEnabled === false ? 2 : 4);
    (window as unknown as Record<string, unknown>).__impactLights = () => ({
      capacity: this.impactLights?.capacity ?? 0, active: this.impactLights?.active ?? 0, ...this.impactLights?.stats,
    });
    impactDecals(app.world.scene); // the shared pool (ships place into it too)
    (window as unknown as Record<string, unknown>).__indoorCombat = () => indoorCombatDiag(); // slice K: what the client's mirror of the judge holds
    try { this.phys = (app.getModule('PhysicsModule') as PhysicsModule | null) ?? null; } catch { this.phys = null; }

    bus.on('atlas:account-ready', () => this.ensureSubs());
    bus.on('atlas:world-cleared', () => this.flushBeasts()); // the despawn sweep only runs in the Commons
    // ONE attack path: F, a pointer-locked left click and the touch button all
    // arrive here (the bounty hunter's fight-back listens to the same event).
    bus.on('atlas:attack', () => {
      this.keyDiag.bus++;
      if (atlas.loadingGate || atlas.spaceFlight || atlas.flyMode) return;
      this.strikeNearest();
    });
    window.addEventListener('keydown', (e) => {
      this.keyDiag.seen++;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.spaceFlight || atlas.flyMode) return;
      // F is COUNTED here for the diagnostic only: the input layer's action
      // map (AtlasInputModule, rebindable) emits `atlas:attack` for it — this
      // listener emitted too, so every press sent TWO strikes and the second
      // was always a free refusal that poisoned `lastError` (probe, 2026-09-18).
      if (e.code === 'KeyF') this.keyDiag.f++;
      // R reloads a magazine gun (flight/space own R for missiles; placement
      // ghosts rotate on R — a gun in hand while placing is rare enough).
      if (e.code === 'KeyR' && !atlas.skyFlight && !atlas.hoverRide && !atlas.editMode && this.equippedWeapon().feed?.kind === 'magazine') {
        atlas.conn?.reducers.reloadWeapon({}).catch((e2: Error) => { this.lastError = `reload: ${String(e2.message ?? e2)}`; });
      }
      if (e.code === 'KeyZ') this.toggleTerminal();
    });

    // Probe/diag: the seed-derived dens (road dens + the flora update's woods dens), mirrored from the server.
    (window as unknown as Record<string, unknown>).__wildsDens = () => {
      const conn = atlas.conn; if (!conn) return [];
      const w = [...conn.db.world.iter()].find((x) => x.kind === 'commons');
      return w ? densFor(w.terrainSeed) : [];
    };
    app.world.addSystem(makeSystem('WildsTickDriver', Phase.FixedSim, 44, () => this.fixed()));
    app.world.addSystem(makeSystem('WildsRender', Phase.Animation, 39, (dt) => this.frame(dt)));
    // COMBAT WAVE diag: feel counters, live telegraphs, stagger, round-trip stats.
    (window as unknown as Record<string, unknown>).__combat = () => ({
      feel: { hitStops: feel.hitStops, kicks: feel.kicks, knockbacks: feel.knockbacks, windups: feel.windups, trauma: +feel.trauma.toFixed(3), shake: feel.shakeEnabled, frozen: frozen(performance.now()) },
      telegraphs: activeWindupCount(),
      telegraphKeys: [...windups.keys()],
      myStaggerLeftMs: Math.max(0, this.myStaggerLeftMs()),
      rtt: rttStats(),
      shotIndex: this.shotIndex,
      projectiles: this.projectiles ? { ...this.projectiles.counters } : null,
      lastError: this.lastError,
      lastStrike: this.lastStrike,
      gate: atlas.loadingGate, inCommons: this.inCommons(), fly: atlas.flyMode, space: atlas.spaceFlight,
      keys: { ...this.keyDiag },
    });
    // Direct lever for probes: the same path the F key takes.
    (window as unknown as Record<string, unknown>).__combatStrike = () => this.strikeNearest();
    // W1 (2026-10-05): the water's answer to a shot along the crosshair, and the last shot that met water.
    (window as unknown as Record<string, unknown>).__waterShot = (kind: WaterShotKind = 'slug') => this.waterShotProbe(kind);
    (window as unknown as Record<string, unknown>).__waterShotLast = () => this.lastWaterShot;
    // Probe levers for the SHOT'S PICTURE: freeze the field mid-flight (a
    // screenshot), step it by hand, and read every live head on screen.
    (window as unknown as Record<string, unknown>).__projectilesFreeze = (on: boolean) => { if (this.projectiles) this.projectiles.frozen = !!on; return !!this.projectiles?.frozen; };
    (window as unknown as Record<string, unknown>).__projectilesStep = (dtS: number) => { if (!this.projectiles) return 0; const was = this.projectiles.frozen; this.projectiles.frozen = false; this.projectiles.update(Math.max(0, Number(dtS) || 0)); this.projectiles.frozen = was; return this.projectiles.counters.live; };
    (window as unknown as Record<string, unknown>).__decals = () => { const d = impactDecalsIfAny(); return d ? { ...d.counters, list: d.list() } : null; };
    (window as unknown as Record<string, unknown>).__decalsAge = (s: number) => { impactDecalsIfAny()?.ageAll(Number(s) || 0); };
    // Stage 3 probe lever: lay one decal of `kind` on the ground under the player (bypasses the gun path's pointer lock).
    (window as unknown as Record<string, unknown>).__decalPlace = (kind: 'hole' | 'scorch' | 'blast' = 'hole') => {
      const scene = this.app?.world.scene, conn = atlas.conn; if (!scene || !conn) return null;
      const g = groundYIn(conn, atlas.currentWorldId, atlas.localX + 1, atlas.localZ + 1);
      return impactDecals(scene).place(kind, new THREE.Vector3(atlas.localX + 1, g, atlas.localZ + 1), new THREE.Vector3(0, 1, 0));
    };
    (window as unknown as Record<string, unknown>).__projectileHeads = (viewportH = 1080) => {
      const cam = this.fpCamera() as THREE.Camera | null;
      return cam && this.projectiles ? this.projectiles.screenHeads(cam, Number(viewportH) || 1080) : [];
    };
    this.buildHudChrome();
    this.installDebug();
  }

  // ── Data ──────────────────────────────────────────────────────────────────
  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }
  /** The table's rows, WALKED — never copied (`tbl` spreads the whole table into an array; fine for a panel, not for a 10 Hz scan). */
  private rows<T>(name: string): Iterable<T> {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? t.iter() : [];
    } catch { return []; }
  }

  private ensureSubs(): void {
    if (this.subbed || !atlas.conn) return;
    this.subbed = true;
    for (const q of ['SELECT * FROM creature', 'SELECT * FROM player_vitals', 'SELECT * FROM mission_hold',
      'SELECT * FROM npc_strike', 'SELECT * FROM npc_combat', 'SELECT * FROM player_action', 'SELECT * FROM weapon_state', 'SELECT * FROM platform_flag',
      'SELECT * FROM player_stance WHERE identity = :sender']) {
      try { atlas.conn.subscriptionBuilder().subscribe([q]); } catch { /* pre-publish */ }
    }
    this.ensureCombatHooks();
  }

  /** COMBAT WAVE: the npc_strike row IS an NPC's telegraph — mirror it into
   *  the shared windup map (creatures, versonas and the hunter all render
   *  from it), bark at the start, and start the round-trip sampler. */
  private ensureCombatHooks(): void {
    const conn = atlas.conn;
    if (this.combatSubbed || !conn) return;
    const db = conn.db as unknown as {
      npcStrike?: { onInsert(cb: (ctx: unknown, row: NpcStrikeRow) => void): void; onDelete(cb: (ctx: unknown, row: NpcStrikeRow) => void): void };
    };
    if (!db.npcStrike) return; // pre-publish bindings
    this.combatSubbed = true;
    db.npcStrike.onInsert((_c, row) => {
      const me = !!conn.identity && row.targetPlayer.isEqual(conn.identity);
      telegraphBegin(row.attackerKey, row.windupMs, me, performance.now());
      // The bark: the tell has a sound before it has a shape.
      const d = Math.hypot(row.x - atlas.localX, row.z - atlas.localZ);
      // AT the attacker (2026-09-20): a tell you can turn toward. Emitter-scoped — two beasts winding up are two barks.
      if (d < 60 && row.attackerKey.startsWith('creature:')) sfxAt('sfx_creature_attack', { x: row.x, y: atlas.localY + 0.9, z: row.z }, { gain: Math.max(0.12, 0.42 - d * 0.005), cooldownMs: 250, emitter: row.attackerKey, caption: 'A creature winds up' });
    });
    db.npcStrike.onDelete((_c, row) => {
      telegraphEnd(row.attackerKey, performance.now());
      // The blow resolved: a versona victim flinches (the skinned layer's Hit_Reaction).
      if (row.targetKind === 'versona') markHurt(`versona:${row.targetVersona}`, performance.now());
    });
    // A blow TAKEN, the moment the row lands (never the 2 Hz HUD poll — a
    // hit-stop half a second late reads as lag, not impact): a beat of
    // hit-stop, the camera nods, a little trauma (shake only if opted in).
    const vit = conn.db as unknown as { playerVitals?: { onUpdate(cb: (ctx: unknown, oldRow: { identity: { isEqual(o: unknown): boolean }; hp: number }, newRow: { identity: { isEqual(o: unknown): boolean }; hp: number }) => void): void } };
    vit.playerVitals?.onUpdate((_c, oldRow, newRow) => {
      if (!conn.identity || !newRow.identity.isEqual(conn.identity) || newRow.hp >= oldRow.hp) return;
      hitStop(performance.now(), oldRow.hp - newRow.hp >= HEAVY_DMG_MIN);
      cameraImpulse(-KICK_TAKEN_PITCH, (Math.random() - 0.5) * 0.02);
      addTrauma(TRAUMA_TAKEN);
    });
    const r = conn.reducers as unknown as { ping?: (a: { nonce: number }) => Promise<void> };
    if (typeof r.ping === 'function') startRttSampler(() => r.ping!({ nonce: nextNonce() }));
  }

  private ensureWorld(): boolean {
    const conn = atlas.conn;
    if (!conn) return false;
    for (const w of this.tbl<{ kind: string; terrainSeed: string }>('world')) {
      if (w.kind !== 'commons') continue;
      if (this.seed !== w.terrainSeed || this.civGen !== civRowGeneration()) {
        this.civGen = civRowGeneration();
        this.seed = w.terrainSeed;
        this.sites = settlementSites(this.seed);
        this.names = settlementNamesFor(this.sites, rowFirstCiv);
      }
      return true;
    }
    return false;
  }

  private inCommons(): boolean {
    return atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
  }

  /** The equipped weapon's catalog spec (FISTS when nothing is equipped) —
   *  drives the fire SOUND (gun crack vs melee whoosh) and the TARGETING
   *  range (the server resolves reach from this same equipment row; the old
   *  hardcoded 7 m made a 46 m Long Rifle a silent no-op past melee range). */
  private equippedWeapon(): WeaponSpec {
    const id = atlas.conn?.identity;
    if (id) {
      try {
        // Through the owner btree (fast_find: a Map lookup, table order kept) — not the shard-wide table ~10×/s.
        for (const eq of atlas.conn!.db.playerEquipment.playerEquipmentByOwner.filter(id)) {
          if (eq.slot === 'weapon' && eq.owner.isEqual(id) && WEAPONS[eq.itemKey]) return WEAPONS[eq.itemKey];
        }
      } catch { /* table mid-churn */ }
    }
    return FISTS;
  }

  private myVitals(): VitalsRow | null {
    const id = atlas.conn?.identity;
    if (!id) return null;
    return this.tbl<VitalsRow>('playerVitals').find((v) => v.identity.isEqual(id)) ?? null;
  }

  private myHolds(): HoldRow[] {
    const id = atlas.conn?.identity;
    if (!id) return [];
    return this.tbl<HoldRow>('missionHold').filter((h) => h.identity.isEqual(id) && h.state === 'active');
  }

  private nearestCreature(maxD: number): { row: CreatureRow; d: number } | null {
    let best: { row: CreatureRow; d: number } | null = null;
    for (const c of this.tbl<CreatureRow>('creature')) {
      if (c.state === 'dead') continue;
      const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
      if (d < maxD && (!best || d < best.d)) best = { row: c, d };
    }
    return best;
  }

  private nearestTerminalSite(): SettlementSite | null {
    let best: SettlementSite | null = null;
    let bd = TERMINAL_RANGE_M;
    for (const s of this.sites) {
      if (s.tier !== 'city' && s.tier !== 'town') continue;
      const d = Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ);
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }

  // ── Fixed loop: tick driver + prompts ────────────────────────────────────
  private fixed(): void {
    this.tickN++;
    if (!this.inCommons() || atlas.loadingGate) {
      // Leaving the Commons (space launch, personal hop) must retract the DOM
      // this 2-Hz refresh owns — the work-terminal prompt stuck on-screen in
      // the space sector because the early return skipped the hide path.
      if (this.prompt) this.prompt.style.display = 'none';
      if (this.hud) this.hud.style.display = 'none';
      return;
    }
    const nearWilds = this.beasts.size > 0 || this.tickN % 600 === 0; // slow scout rate wakes distant dens
    if (this.tickN % TICK_EVERY === 0 && nearWilds) {
      this.ensureSubs();
      atlas.conn?.reducers.wildsTick({}).catch(() => { /* guarded server-side */ });
    }
    if (this.tickN % 30 !== 0) return;
    if (!this.ensureWorld()) return;
    // Prompts + HUD refresh at ~2 Hz.
    const near = this.nearestCreature(STRIKE_RANGE_M);
    const terminal = this.nearestTerminalSite();
    if (this.prompt) {
      const lines: string[] = [];
      if (near) lines.push(`<b>F</b> — strike the ${esc(pretty(near.row.species))} (${near.row.hp} hp)`);
      if (terminal) lines.push(`<b>Z</b> — work terminal · ${esc(this.names.get(terminal.id) ?? '')}`);
      this.prompt.innerHTML = lines.join('<br>');
      this.prompt.style.display = lines.length && !atlas.spaceFlight ? 'block' : 'none'; // ground-only (ladder v2)
    }
    const v = this.myVitals();
    const hp = v?.hp ?? PLAYER_MAX_HP;
    if (hp < this.lastHp && this.flash) {
      // (the hit-stop / camera kick / trauma for a blow TAKEN fire on the
      // vitals ROW update in ensureCombatHooks — frame-exact, not this 2 Hz poll)
      this.flash.style.opacity = '0.45';
      window.requestAnimationFrame(() => { if (this.flash) this.flash.style.opacity = '0'; });
      sfx('sfx_player_hurt', { gain: 0.5, cooldownMs: 250 }); // the damage vignette gets an audible sting
      // The maul itself sounds AT the beast: the nearest aggro creature in
      // claw range is the one that just hit you.
      const mauler = this.nearestCreature(8);
      if (mauler && mauler.row.state === 'aggro') sfx('sfx_creature_attack', { gain: 0.5, cooldownMs: 350 });
      // A killing blow never reads as an hp DROP (the server resets hp to max
      // in the same write) — but a lethal-range hit heard here still stings.
    }
    // DEATH + medbay respawn: the server kills you by resetting hp → max and
    // teleporting you to the arrival pad, so the client's tell is hp JUMPING
    // UP from critical to full in one poll. Play the swallow + the wake chime.
    if (this.lastHp <= 25 && hp >= PLAYER_MAX_HP) {
      sfx('sfx_player_death', { gain: 0.55, cooldownMs: 2000 });
      sfxLater(1400, () => sfx('sfx_respawn', { gain: 0.4, cooldownMs: 2000 })); // dies with the world it was scheduled in
    }
    // Low-HP heartbeat loop: ramps in under 35 hp, silent above.
    sfxLoop('sfx_low_hp', 'player').set(hp < 35 && hp > 0 ? Math.min(0.3, (35 - hp) * 0.012) : 0, 1, 0.5);
    this.lastHp = hp;
    if (this.hud) {
      const show = (hp < PLAYER_MAX_HP || near !== null) && !atlas.spaceFlight; // ground-only (ladder v2)
      this.hud.style.display = show ? 'block' : 'none';
      if (show) {
        this.hud.innerHTML =
          `HP <span style="display:inline-block;width:120px;height:8px;background:var(--ax-bg3);border-radius:var(--ax-radius);vertical-align:1px">` +
          `<span style="display:block;width:${Math.max(0, hp)}%;height:100%;border-radius:var(--ax-radius);background:${hp > 35 ? 'var(--ax-good)' : 'var(--ax-bad)'}"></span></span> ${hp}/${PLAYER_MAX_HP}` +
          (this.myStaggerLeftMs() > 0 ? ' <span style="color:var(--ax-warn);font-weight:700">REELING</span>' : '') +
          (this.lastWhiffFlash > performance.now() ? ' <span style="color:var(--ax-soft)">swung wide</span>' : '');
      }
    }
    if (this.tickN % 150 === 0 && this.win?.isOpen()) this.renderTerminal(); // ~2.5 s — the board changes slowly
  }

  // ── Soft target (build-now wave 5): the mark the next attack would take ──
  /** A townsperson drawn more than this above or below my feet stands on another storey (a storey is 3.4 m). */
  private static readonly STOREY_GATE_M = 1.7;
  private reticleAcc = 0;
  private lastMarkKey = '';
  private readonly _proj = new THREE.Vector3();
  /** The same cone-and-reach pick the strike makes, ~10 Hz, projected to the
   *  screen for the HUD's reticle: what the player is about to hit is never a
   *  surprise (the guide's §35.3 soft lock — no camera pull, just the mark).
   *  Emits null once when nothing is in the cone. */
  private updateSoftTarget(): void {
    const conn = atlas.conn;
    const cam = this.fpCamera() as THREE.PerspectiveCamera | null;
    const scene = this.app?.world.scene;
    const live = !!conn && !!cam && !!scene && this.inCommons() && !atlas.hoverRide && !atlas.flyMode
      && !atlas.spaceFlight && !atlas.skyFlight && !atlas.xrPresenting && !atlas.loadingGate && !atlas.editMode;
    if (!live) { this.clearSoftTarget(); return; }
    const spec = this.equippedWeapon();
    const cands = this.gatherCands(conn);
    // STABLE + LEGAL (ground PvP wave): legality is filtered before ranking,
    // and the mark holds until another candidate is meaningfully better.
    const i = pickTargetStable(atlas.localX, atlas.localZ, atlas.localYaw, spec.rangeM, spec.kind, cands, this.lastMarkKey);
    if (i < 0) { this.clearSoftTarget(); return; }
    const c = cands[i];
    const y = c.y ?? groundYIn(conn, atlas.currentWorldId, c.x, c.z) + 1.0;
    // Candidates are ABSOLUTE; the camera lives in render space (the FP64
    // rebase: scene.position = −origin) — shift before projecting.
    this._proj.set(c.x + scene.position.x, y + scene.position.y, c.z + scene.position.z).project(cam);
    if (this._proj.z > 1 || this._proj.z < -1) { this.clearSoftTarget(); return; }
    this.lastMarkKey = `${c.kind}:${c.id}`;
    // The BLOCKED cue: the client's own surface query between muzzle and
    // mark — a prediction of the server's wall-grid verdict, never its
    // authority (the result row says 'blocked' when the shot was spent on a wall).
    // INDOOR COMBAT (slice K, 2026-10-02): where a presence row puts either of us indoors, the mark is the SAME pure
    // rule the server judges with (lib/indoor_combat.ts) — another floor, the shell, a partition, cover — for every
    // kind of mark, and it says why. With nobody indoors (`street`) the surface query below predicts as before.
    let blocked = false;
    let label = c.label;
    const sight = clientSight({ kind: c.kind, id: c.id, x: c.x, z: c.z }, spec.kind === 'gun');
    if (sight.kind === 'blocked') { blocked = true; label = `${c.label} · ${sight.words}`; }
    else if (sight.kind === 'cover') {
      // crouched behind it the shot is spent on the cover; standing, the cover takes half (the server's coverEffect)
      const crouched = c.kind === 'player' && atlas.remotes.get(c.id)?.animState === 'crouch';
      if (crouched) { blocked = true; label = `${c.label} · behind cover`; } else label = `${c.label} · in cover`;
    } else if (sight.kind === 'street' && c.kind === 'player') {
      this.muzzleWorld(this._muzzle);
      this._mark.set(c.x, y, c.z);
      const dist = this._muzzle.distanceTo(this._mark);
      const surface = this.surfaceHit(this._muzzle, this._mark, dist + 1);
      blocked = !!surface && surface.dist < dist - 0.4;
    }
    const actor = myActor();
    bus.emit('atlas:soft-target', {
      x: (this._proj.x + 1) / 2 * window.innerWidth, y: (1 - this._proj.y) / 2 * window.innerHeight, label, kind: c.kind, id: c.id,
      blocked, committed: !!actor && actor.pendingActionId !== 0n,
    });
  }
  private clearSoftTarget(): void {
    if (!this.lastMarkKey) return;
    this.lastMarkKey = '';
    bus.emit('atlas:soft-target', null);
  }

  /** Every shootable thing (build-now wave 5 pulled this out of the strike so
   *  the soft-target reticle can pick the same way): creatures, townspeople
   *  within 80 m, the hunter's body, other players when PvP is on. */
  private gatherCands(conn: NonNullable<typeof atlas.conn>): StrikeCand[] {
    const cands: StrikeCand[] = [];
    const commons = this.inCommons();
    ensureIndoorClient(); // slice K: the presence rows the indoor rule reads (idempotent)
    // THE INDOOR-TARGET LAW, for the attack (slice K — the talk pick has kept it since 2026-09-22): a townsperson DRAWN
    // in another building than the one I stand in (or indoors while I am out) is behind a wall, and one drawn on
    // another storey of my building is behind a floor — neither is offered. A body not in the drawn set is offered as
    // before (the server judges the row).
    const env = this.app?.getModule('Environment') as { versonaSeat?: (id: string) => { indoors: string | null; y: number } | undefined } | undefined;
    const myRoom = atlas.interiorId !== null && enterables.has(atlas.interiorId) ? atlas.interiorId : null;
    const seatOfVersona = (id: string): { offer: boolean; y: number | null } => {
      const st = env?.versonaSeat?.(id);
      if (!st) return { offer: true, y: null };
      if (st.indoors !== myRoom) return { offer: false, y: null };
      if (st.indoors === null) return { offer: true, y: null };
      return { offer: Math.abs(st.y - atlas.localY) <= WildsModule.STOREY_GATE_M, y: st.y + 1.0 };
    };
    if (commons) {
      for (const c of this.tbl<CreatureRow>('creature')) {
        if (c.state === 'dead') continue;
        const b = this.beasts.get(String(c.creatureId));
        cands.push({
          kind: 'creature', id: String(c.creatureId), label: pretty(c.species), x: c.x, z: c.z, y: b ? b.root.position.y + 0.9 : null,
          fire: () => conn.reducers.strikeCreature({ creatureId: c.creatureId }),
        });
      }
      // Townspeople: the hot (AOI-windowed) rows when the split has them, else the fat rows in this world.
      // This runs ~10× a second for the reticle. WHY the shape (2026-09-20): it used to spread the WHOLE fat versona
      // table (11.7k rows, a string per row) on every call to find bodies with no hot row — and in a world that HAS
      // hot rows there are none: a versona is drawn only while its hot row exists, so nothing else can be aimed at.
      let hotHere = 0;
      for (const h of this.rows<VersonaHotRow>('versonaState')) {
        if (atlas.currentWorldId !== null && h.worldId !== atlas.currentWorldId) continue;
        hotHere++;
        if (Math.hypot(h.curX - atlas.localX, h.curZ - atlas.localZ) > 80) continue;
        const vs = seatOfVersona(String(h.versonaId));
        if (!vs.offer) continue;
        cands.push({ kind: 'versona', id: String(h.versonaId), label: nameFromTables(conn, h.versonaId), x: h.curX, z: h.curZ, y: vs.y, fire: () => conn.reducers.strikeVersona({ versonaId: h.versonaId }) });
      }
      // A world with no hot rows at all (a server older than the split, a small personal world): the fat rows stand in.
      if (hotHere === 0) for (const v of this.rows<VersonaHotRow>('versona')) {
        if (atlas.currentWorldId !== null && v.worldId !== atlas.currentWorldId) continue;
        if (Math.hypot(v.curX - atlas.localX, v.curZ - atlas.localZ) > 80) continue;
        cands.push({ kind: 'versona', id: String(v.versonaId), label: nameFromTables(conn, v.versonaId), x: v.curX, z: v.curZ, y: null, fire: () => conn.reducers.strikeVersona({ versonaId: v.versonaId }) });
      }
    }
    for (const g of groundTargetList()) {
      cands.push({ kind: g.kind, id: g.id, label: g.label, x: g.x, z: g.z, y: g.y + 1.0, fire: g.strike });
    }
    if (this.pvpEnabled()) {
      // Ground PvP wave (2026-09-18): the flag alone offers nobody — only the
      // players the POLICY's mirror says I may lawfully strike right now (my
      // duel's opponent, my match's live opposition, the sector's other side).
      const legal = legalPlayerTargets();
      for (const r of atlas.remotes.values()) {
        if (!legal.has(r.identityHex)) continue;
        // (slice K) a traveler the indoor rule does not DRAW from here — behind a building's shell, on another
        // storey seen from outside — is not offered either: the reticle must not point through a wall at a body
        // the picture hides. In my own building everyone is drawn, on their floor, and judged by the rule.
        if (remoteSeat(r.identityHex, r.targetX, r.targetZ).hidden) continue;
        cands.push({
          kind: 'player', id: r.identityHex, label: playerName(r.identityHex), x: r.targetX, z: r.targetZ, y: r.mesh ? r.mesh.position.y + 0.9 : null,
          fire: () => conn.reducers.strikePlayer({ target: Identity.fromString(r.identityHex) }), legal: true,
        });
      }
    }
    for (const c of cands) c.key = `${c.kind}:${c.id}`;
    return cands;
  }

  /** ONE attack path (F / locked click / touch). Every shootable thing on the
   *  ground is a candidate — creatures, townspeople (versonas), the bounty
   *  hunter's body (ground_targets), other players when `pvp_enabled` — picked
   *  by the same cone-and-reach rule the server judges with. The gun's VOICE
   *  and its PROJECTILE fire on every shot whether or not anything stands in
   *  the way (2026-09-07: "I do not see a projectile come out and there is no
   *  noise" — the old path returned before either when no creature was in
   *  reach), and a shot at nothing still spends its round (fireWeapon). */
  private strikeNearest(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const spec = this.equippedWeapon();
    const cands = this.gatherCands(conn);
    // The pick: nearest inside the facing cone + padded reach; with nothing in
    // the cone but something in reach, the swing still goes at it (it WHIFFS
    // server-side — the commitment price, not a free retry).
    let i = pickTargetStable(atlas.localX, atlas.localZ, atlas.localYaw, spec.rangeM, spec.kind, cands, this.lastMarkKey);
    const picked = i >= 0;
    if (i < 0) {
      let bd = spec.rangeM + REACH_PAD_M;
      for (let k = 0; k < cands.length; k++) {
        if (cands[k].legal === false) continue;
        const d = Math.hypot(cands[k].x - atlas.localX, cands[k].z - atlas.localZ);
        if (d < bd) { bd = d; i = k; }
      }
    }
    const target = i >= 0 ? cands[i] : null;
    const nowMs = performance.now();
    this.lastStrike = {
      kind: target?.kind ?? 'none', id: target?.id ?? '', creatureId: target?.kind === 'creature' ? target.id : '', picked, at: nowMs,
      d: target ? Math.hypot(target.x - atlas.localX, target.z - atlas.localZ) : -1, voice: '', projectile: false,
    };
    if (spec.kind === 'gun') {
      // The FEED (2026-09-05): an empty magazine / a dry capacitor is a dry
      // click, not a round trip — the server would refuse it the same way.
      const feedWhy = this.feedBlock(spec);
      if (feedWhy) {
        sfx('sfx_dry_fire', { gain: 0.5, cooldownMs: 150 });
        this.lastError = feedWhy;
        this.lastStrike.voice = 'sfx_dry_fire';
        return;
      }
      // Honest recoil: a deterministic per-shot pattern kicks the camera —
      // first shot, then the learnable walk; idle for 1.5× the cadence resets it.
      if (nowMs - this.lastShotMs > spec.cooldownMs * 1.5) this.shotIndex = 0;
      const r = recoilFor(spec.recoil, this.shotIndex++);
      cameraImpulse(r.pitch, r.yaw);
      this.lastShotMs = nowMs;
      // Every gun has its own voice (gen-sfx: sfx_gun_<key>) and its own shot,
      // drawn from the muzzle to the mark's torso — or, with nothing in the
      // way, out along the aim to the weapon's range.
      const voice = `sfx_gun_${spec.key}`;
      sfx(voice, { gain: 0.5, cooldownMs: 120 });
      this.lastStrike.voice = voice;
      if (spec.projectile && this.projectiles) {
        this.projectiles.setCamera(this.fpCamera());
        this.muzzleWorld(this._muzzle);
        if (target && picked) {
          // In the cone: the shot flies to the mark's torso.
          const y = target.y ?? (target.kind === 'versona' || target.kind === 'hunter' ? groundYIn(conn, atlas.currentWorldId, target.x, target.z) + 1.0 : atlas.localY + 0.9);
          this._mark.set(target.x, y, target.z);
        } else {
          // Nothing in the cone (a fallback target beside or behind you whiffs
          // server-side): the shot flies where you are LOOKING, never backwards.
          // OUT TO THE VISUAL RANGE, not the weapon's (2026-09-15): damage
          // still stops at `rangeM` — the server judges that — but a bolt that
          // hits nothing should carry on until it meets something or leaves the
          // frame, not stop dead in mid-air at the edge of its damage envelope.
          this.aimPoint(this._mark, VISUAL_RANGE_M);
        }
        const ballistic = spec.projectile.kind === 'slug' || spec.projectile.kind === 'pellets' || spec.projectile.kind === 'shell';
        // WHERE IT LANDS (2026-09-08): the first surface on the way — physics
        // (terrain trimesh, building proxies) else the analytic ground — ends
        // the flight there and takes the mark: a bullet HOLE (ballistic) or a
        // SCORCH (energy), gone after its life. A shot that reaches flesh first
        // leaves no mark.
        // The surface query reaches as far as the bolt is allowed to FLY, so a
        // miss that eventually meets a hillside 400 m out still lands on it.
        let surface = this.surfaceHit(this._muzzle, this._mark, VISUAL_RANGE_M);
        const toMark = this._muzzle.distanceTo(this._mark);
        let marks = !!surface && (!(target && picked) || surface.dist < toMark - 0.4);
        // INDOOR COMBAT (slice K): a CROUCHED mark behind cover is not hit — the server spends the shot on the cover
        // (`coverEffect`). The line to a torso clears a 0.96 m desk, so the physics ray finds nothing: the bolt is
        // ended where the pure rule says the line meets the piece, at its top edge, and takes its mark there.
        if (!marks && target && picked && target.kind === 'player' && atlas.remotes.get(target.id)?.animState === 'crouch') {
          const cov = clientSight({ kind: target.kind, id: target.id, x: target.x, z: target.z }, true);
          if (cov.kind === 'cover') {
            const feetY = this._mark.y - 0.9;
            this._hitP.set(cov.at.x, feetY + Math.max(0.2, cov.height - 0.06), cov.at.z);
            this._hitN.set(this._muzzle.x - cov.at.x, 0, this._muzzle.z - cov.at.z);
            if (this._hitN.lengthSq() < 1e-6) this._hitN.set(0, 1, 0);
            this._hitN.normalize();
            surface = { dist: this._muzzle.distanceTo(this._hitP), kind: 'structure' };
            marks = true;
            this.lastStrike.cover = true;
          }
        }
        // W1 (2026-10-05): THE WATER ENDS A SHOT TOO. `surfaceHit` casts against the Rapier colliders and the analytic
        // ground and water is neither, so a bolt fired into a lake or the sea flew to the BED and left a hole, a dust puff
        // and a stone's voice under the water. March the shot against the still level up to the first solid it meets
        // (water lies above its bed, so a crossing past that solid is never the first thing struck): a bolt that meets
        // the water first ends there — a splash, a ring, a plop or a hiss, NOTHING on the bed. A body in the cone keeps
        // its hit (a swimmer's torso is under the surface): the crossing on the way is its ENTRY splash.
        const bodyPicked = !!(target && picked);
        let waterAt: { x: number; y: number; z: number } | null = null;
        let entryAt: { x: number; y: number; z: number } | null = null;
        if (atlas.interiorId === null) {
          const water = this.waterCrossing(bodyPicked && !marks ? toMark : surface ? surface.dist : VISUAL_RANGE_M);
          if (water) {
            const wy = waterLiveAt(water.x, water.z) ?? water.y;
            if (bodyPicked && !marks) entryAt = { x: water.x, y: wy, z: water.z };
            else { waterAt = { x: water.x, y: wy, z: water.z }; marks = false; surface = null; this._mark.set(water.x, wy, water.z); }
          }
        }
        if (marks) this._mark.copy(this._hitP);
        // INDOORS the room is UNLIT (interiors.md): a point light there lights nothing and a lit decal answers a sun
        // the wall never sees — the mark is the flat flavour, the flash is the bolt's own burst, and nothing is
        // written into the session's material damage (nothing a player owns is marked by a fight past the decal's life).
        const indoorsShot = atlas.interiorId !== null && !!surface && surface.kind !== 'terrain' && surface.kind !== 'ground';
        const decalKind: DecalKind = ballistic ? 'hole' : 'scorch';
        const decalAt = marks ? this._hitP.clone() : null, decalN = marks ? this._hitN.clone() : null;
        const decalMul = spec.projectile.kind === 'pellets' ? 0.7 : spec.projectile.kind === 'shell' ? 2.2 : spec.projectile.kind === 'lance' ? 1.4 : 1;
        this.lastStrike.surface = waterAt ? 'water' : surface ? surface.kind : null;
        // THE SURFACE DECIDES THE IMPACT (2026-09-15). `surfaceHit` already
        // knew what was struck — the collider class, and for ground the seeded
        // biome — and threw it away on one of two sounds. Now it picks the
        // whole response: flash, light, ejecta, dust, mark and voice. The
        // profile is scaled by how square-on the hit was, so a graze does not
        // throw the same plume as a shot straight into the wall.
        const inc = (surface && decalN) ? incidence(
          { x: this._aimDir.x, y: this._aimDir.y, z: this._aimDir.z },
          { x: decalN.x, y: decalN.y, z: decalN.z }) : 1;
        const hitBody = !!(target && picked) && !marks;
        const profile = scaleProfile(
          impactProfile(surface ? surface.kind : null, this.biomeAtHit(this._mark), hitBody),
          inc, decalMul);
        // Did the bolt MEET anything? A body in the cone, or a surface. If
        // neither, it flies its full visual range and arrives at nothing.
        // W1: the WATER is something too — `projectiles` skips a miss's arrival callback entirely, so a water hit with
        // `marks` cleared would have flown silently through the splash it is owed.
        const meetsSomething = marks || !!(target && picked) || !!waterAt;
        // WHERE it lands, captured NOW: `_mark` is a reused scratch vector, and the callback runs when the bolt arrives.
        const hitAt = { x: this._mark.x, y: this._mark.y, z: this._mark.z };
        const shotKind = spec.projectile.kind as WaterShotKind;
        this.projectiles.fire(spec.projectile, this._muzzle, this._mark, () => {
          if (waterAt) {
            // W1: the bolt meets the water — the water answers, on the bolt's clock; the flinch (if any) still lands.
            this.waterImpact(waterAt.x, waterAt.y, waterAt.z, shotKind, false);
            bus.emit('atlas:impact', { x: waterAt.x, y: waterAt.y, z: waterAt.z });
            const arrivedW = this.onBoltArrived;
            this.onBoltArrived = null;
            arrivedW?.();
            return;
          }
          if (entryAt) this.waterImpact(entryAt.x, entryAt.y, entryAt.z, shotKind, true);
          // EVERYTHING ON THE BOLT'S CLOCK. Damage is authoritative and
          // instant, but nothing the player SEES may precede the bolt.
          // The material voice, falling back to the generic pair when a bank
          // has no clip for it (the audio wave's ORPHAN-ZONE law: never silent).
          // AT the impact. The five material clips exist in no catalog — the runtime synthesizes them (lib/audio/synth),
          // so metal rings, stone cracks and soil thumps for the first time; a recorded clip installed later simply wins.
          sfxAt(profile.sfx.key, hitAt, { gain: profile.sfx.gain, cooldownMs: 90 });
          sfxAt(ballistic ? 'sfx_impact_slug' : 'sfx_impact_energy', hitAt, { gain: profile.sfx.gain * 0.45, cooldownMs: 90 });
          if (profile.light && !indoorsShot) {
            this.impactLights?.request({
              x: this._mark.x, y: this._mark.y, z: this._mark.z,
              color: profile.light.color, intensity: profile.light.intensity,
              rangeM: profile.light.rangeM, ms: profile.light.ms,
            }, { x: atlas.localX, y: atlas.localY, z: atlas.localZ });
          }
          // Lit smoke (2026-09-15): the surface's dust as a six-way lit puff, on
          // the bolt's clock like everything else the player sees.
          if (profile.dust) litSmokePuff(this._mark.x, this._mark.y, this._mark.z, profile.dust);
          // Attention priority (2026-09-15): the landing turns nearby heads toward it.
          bus.emit('atlas:impact', { x: this._mark.x, y: this._mark.y, z: this._mark.z });
          if (decalAt && decalN && profile.decal !== 'none') {
            // Oblique hits stretch their mark along the incoming direction,
            // clamped so a graze cannot paint a hundred-metre smear.
            impactDecalsIfAny()?.place(decalKind, decalAt, decalN, { sizeMul: decalMul * decalStretch(inc), lifeS: damageDecalLifeS(surface ? surface.kind : null, decalAt), flat: indoorsShot });
          }
          // shader gaps R2 (2026-09-29): the hit changes the SURFACE — stripped paint, char, a gouge (lib/damage_*.ts);
          // the decal above is the fresh soot, this stays for the session. Absolute coordinates, like the decal.
          if (decalAt && decalN && !indoorsShot) reportDamageHit({ x: decalAt.x, y: decalAt.y, z: decalAt.z, nx: decalN.x, ny: decalN.y, nz: decalN.z, weapon: ballistic ? 'kinetic' : 'energy', collider: surface ? surface.kind : null, biome: this.biomeAtHit(decalAt), punch: decalMul, energy: 0.6 + 0.4 * inc });
          // Fire ONCE and clear: a later bolt must not replay a stale flinch.
          const arrived = this.onBoltArrived;
          this.onBoltArrived = null;
          arrived?.();
        }, meetsSomething);
        this.lastStrike.projectile = true;
      }
    } else {
      // Bare hands keep the generic swing; every crafted blade has its own.
      const voice = spec.key ? `sfx_melee_${spec.key}` : 'sfx_weapon_swing';
      sfx(voice, { gain: 0.5, cooldownMs: 220 }); // the swing sounds NOW; the hit lands via the hp-drop cue
      this.lastStrike.voice = voice;
    }
    if (!target) {
      // A shot into the dark: the round still leaves the gun, the swing still whiffs.
      conn.reducers.fireWeapon({}).then(() => { this.lastError = ''; }).catch((e: Error) => { this.lastError = `fire: ${String(e.message ?? e)}`; });
      return;
    }
    if (target.kind === 'versona') {
      // THE FLINCH WAITS FOR THE BOLT (2026-09-15). The server judges the
      // strike the instant it is fired, so this used to play the hurt overlay
      // at t=0 while the bolt was still a quarter of a second out — the target
      // reacting before it was hit, which is the one arrangement that reads as
      // broken however good the bolt looks. Authority stays instant; the
      // PRESENTATION is queued onto the bolt's arrival. With no projectile
      // (melee, or a weapon with no spec) it still reads immediately.
      const id = target.id;
      // …and the struck versona's ATTENTION goes to the player (threat rung).
      if (this.lastStrike.projectile) this.onBoltArrived = () => { const t2 = performance.now(); markHurt('versona:' + id, t2); markStruckByPlayer('versona:' + id, t2); };
      else { markHurt('versona:' + id, nowMs); markStruckByPlayer('versona:' + id, nowMs); }
      if (nowMs - (this.versonaToastAt.get(target.id) ?? -1e9) > 15_000) {
        this.versonaToastAt.set(target.id, nowMs);
        bus.emit('atlas:status', { text: `You ${spec.kind === 'gun' ? 'shot' : 'struck'} ${target.label}. They will not forget it.` });
      }
    }
    target.fire().then(() => { this.lastError = ''; }).catch((e: Error) => { this.lastError = `strike: ${String(e.message ?? e)}`; });
  }

  /** `pvp_enabled` on this shard (the platform_flag row), else other players are never marks. */
  private pvpEnabled(): boolean {
    try {
      const f = (atlas.conn?.db as unknown as { platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } } })?.platformFlag?.key.find('pvp_enabled');
      return !!f && f.enabled;
    } catch { return false; }
  }

  /** The first surface between `from` and `to` (extended to `maxM`): the
   *  physics world (terrain trimesh / building proxies, normal included) when
   *  it is ready, else the analytic ground walked along the ray. Writes
   *  `_hitP` / `_hitN`; returns the distance and the surface class. */
  /**
   * The seeded biome at a hit, which is what decides a ground impact's
   * material. Derived from the world seed, so it is stable across asset
   * re-cooks and re-textures — unlike reading the display material.
   */
  private biomeAtHit(p: THREE.Vector3): string | null {
    if (!this.seed) return null;
    try { return biomeAt(this.seed, p.x, p.z); } catch { return null; }
  }

  private surfaceHit(from: THREE.Vector3, to: THREE.Vector3, maxM: number): { dist: number; kind: string } | null {
    this._aimDir.subVectors(to, from);
    if (this._aimDir.lengthSq() < 1e-6) return null;
    this._aimDir.normalize();
    const ph = this.phys?.rayHit({ x: from.x, y: from.y, z: from.z }, { x: this._aimDir.x, y: this._aimDir.y, z: this._aimDir.z }, maxM);
    if (ph) {
      this._hitP.set(ph.x, ph.y, ph.z); this._hitN.set(ph.nx, ph.ny, ph.nz);
      return { dist: ph.dist, kind: ph.kind };
    }
    const conn = atlas.conn;
    if (!conn) return null;
    const wid = atlas.currentWorldId;
    const heightAt = (x: number, z: number): number => groundYIn(conn, wid, x, z);
    const g = rayGroundHit(heightAt, from.x, from.y, from.z, this._aimDir.x, this._aimDir.y, this._aimDir.z, maxM);
    if (!g) return null;
    const n = groundNormal(heightAt, g.x, g.z);
    this._hitP.set(g.x, g.y, g.z); this._hitN.set(n.x, n.y, n.z);
    return { dist: g.t, kind: 'ground' };
  }

  /**
   * W1: the first point where the shot's ray (`_muzzle` along `_aimDir`, which `surfaceHit` just set) ENTERS the water
   * within `maxM` — sea, lake or river, the STILL level through the water provider (lib/water_interact.ts). Absolute.
   */
  private waterCrossing(maxM: number): WaterCrossing | null {
    const m = this._muzzle, d = this._aimDir;
    if (d.lengthSq() < 0.5) return null;
    return firstWaterCrossing(m.x, m.y, m.z, d.x, d.y, d.z, maxM, waterStillAt);
  }

  /**
   * W1: a shot meeting the water — a splash (particles, the particle budget's gate), a ring (the fluid flag's gate) and
   * the water's voice by projectile (a slug plops, energy hisses; sounds on every tier). `entry` = the bolt passing the
   * surface on its way to a body in the water: half the splash, no decal ever. ABSOLUTE coordinates; ONE DISTANCE OWNER
   * (the call site's gain × inverseDistanceGain — the panner gives direction only).
   */
  private waterImpact(x: number, y: number, z: number, kind: WaterShotKind, entry: boolean): void {
    const w = waterShotFor(kind);
    const k = entry ? 0.5 : 1;
    splashWater(x, y, z, Math.max(3, Math.round(w.count * k)), w.speed);
    stampWater(x, z, w.stamp.strength * k, w.stamp.radius);
    const d = Math.hypot(x - atlas.localX, y - atlas.localY, z - atlas.localZ);
    const g = w.gain * (entry ? 0.6 : 1) * inverseDistanceGain(d, WATER_SHOT_REF_M, WATER_SHOT_MAX_M);
    if (g > 0.004) sfxAt(w.key, { x, y, z }, { gain: g, rate: w.rate * (0.94 + Math.random() * 0.12), cooldownMs: 50, caption: 'Splash' });
    const ia = waterFx().interact;
    if (entry) ia.bodyEntries++; else ia.shots++;
    this.lastWaterShot = { x, y, z, kind, entry, at: performance.now() };
  }
  private lastWaterShot: { x: number; y: number; z: number; kind: string; entry: boolean; at: number } | null = null;

  /**
   * Probe lever (`__waterShot`): fire the W1 path along the CROSSHAIR without the gun — the same `surfaceHit`, the same
   * crossing, the same impact (the projectile flight and the reducer are the real strike's; this proves the water side).
   */
  private waterShotProbe(kind: WaterShotKind): object {
    this.muzzleWorld(this._muzzle);
    this.aimPoint(this._mark, VISUAL_RANGE_M);
    const surface = this.surfaceHit(this._muzzle, this._mark, VISUAL_RANGE_M);
    const water = atlas.interiorId === null ? this.waterCrossing(surface ? surface.dist : VISUAL_RANGE_M) : null;
    if (water) this.waterImpact(water.x, waterLiveAt(water.x, water.z) ?? water.y, water.z, kind, false);
    return { surface: surface ? { kind: surface.kind, dist: +surface.dist.toFixed(2) } : null, water, interact: { ...waterFx().interact }, last: this.lastWaterShot };
  }

  /**
   * THE FP64 REBASE. `getWorldPosition` / `localToWorld` on anything under the
   * scene return a RENDER-space point, because `scene.position` is MINUS the
   * current origin. Everything else on the shot path is ABSOLUTE — the DB rows
   * a target comes from, the Rapier colliders the surface ray is cast against,
   * the analytic terrain, the biome lookup, the impact lights, the decals, and
   * the projectile quads themselves (scene children, so their `position` IS an
   * absolute coordinate). Mixing the two is invisible within 10 km of the
   * origin, where the rebase has not fired and the frames coincide — which is
   * where every probe runs — and past it the bolt is drawn a whole origin away
   * (2026-09-15: "I still visually do not see the projectile come out of my
   * gun"). Convert at the boundary; the shot path is absolute throughout.
   */
  private toAbsolute(v: THREE.Vector3): THREE.Vector3 {
    const s = this.app?.world.scene.position;
    return s ? v.sub(s) : v;
  }

  /** The point a shot at nothing flies to: `rangeM` along the CROSSHAIR (the
   *  eye's look line, not the muzzle's) — so every tracer converges on the
   *  reticle the way an FPS shot reads, instead of running parallel to it.
   *  ABSOLUTE coordinates (see `toAbsolute`). */
  private aimPoint(out: THREE.Vector3, rangeM: number): THREE.Vector3 {
    const cam = this.fpCamera();
    if (cam) { cam.getWorldDirection(this._aimDir); cam.getWorldPosition(out); this.toAbsolute(out); }
    else { this._aimDir.set(-Math.sin(atlas.localYaw), 0, -Math.cos(atlas.localYaw)); out.set(atlas.localX, atlas.localY + 1.5, atlas.localZ); }
    return out.addScaledVector(this._aimDir, Math.max(8, rangeM));
  }

  /** Why the gun in hand cannot fire now ('' = it can): the local mirror of
   *  the server's feed verdict on the weapon_state row. */
  private feedBlock(spec: { key: string; feed?: { kind: 'magazine'; rounds: number; reloadMs: number } | { kind: 'energy'; chargeMax: number; shotCost: number; rechargePerS: number } }): string {
    const feed = spec.feed;
    const conn = atlas.conn;
    if (!feed || !conn?.identity) return '';
    let row: WeaponStateRow | null = null;
    for (const w of this.tbl<WeaponStateRow>('weaponState')) if (w.identity.isEqual(conn.identity)) { row = w; break; }
    if (!row || row.itemKey !== spec.key) return ''; // no row yet: a full feed
    const nowMs = Date.now();
    const reloadUntil = Number(row.reloadUntil.microsSinceUnixEpoch / 1000n);
    const leftMs = reloadUntil > 0 ? Math.max(0, reloadUntil - nowMs) : 0;
    const rounds = reloadUntil > 0 && leftMs <= 0 && feed.kind === 'magazine' ? feed.rounds : row.rounds;
    const charge = feed.kind === 'energy' ? chargeAfter(row.charge, feed, nowMs - Number(row.chargeAt.microsSinceUnixEpoch / 1000n)) : 0;
    const v = feedReady(feed, { rounds, charge, reloadLeftMs: leftMs });
    return v.ok ? '' : `${v.why === 'reloading' ? 'Reloading' : 'Recharging'} (${(v.msLeft / 1000).toFixed(1)} s)`;
  }

  /** The first-person camera under the rig (the one the view model hangs from). */
  private fpCamera(): THREE.Object3D | null {
    const rig = atlas.localMesh;
    return (rig?.children.find((c) => (c as { isCamera?: boolean }).isCamera) as THREE.Object3D | undefined) ?? null;
  }

  /** The gun's muzzle in world space: just PAST the view model's barrel tip
   *  (the pivot is the model's centre; a gun is ≤ 0.62 m long — 0.26 m ahead
   *  put the flash inside the barrel), else just ahead of the eye. */
  private muzzleWorld(out: THREE.Vector3): THREE.Vector3 {
    const cam = this.fpCamera();
    const vm = cam?.getObjectByName('weapon-view-model') ?? null;
    // `localToWorld` walks the scene, so both branches land in RENDER space and
    // have to come back to ABSOLUTE (see `toAbsolute`); the last one already is.
    if (vm?.visible) { out.set(0, 0.02, -0.36); vm.localToWorld(out); return this.toAbsolute(out); }
    // NO VIEW MODEL IN THE PICTURE. In third person it is hidden and it hangs
    // off the CAMERA, which orbits up to 8 m behind the avatar — a bolt seated
    // there leaves the viewer's face, not the gun. Fire from my body instead
    // (already absolute: it is built from the player's own transform).
    const b = bodyMuzzle(atlas.localYaw, atlas.localX, atlas.localY, atlas.localZ);
    return out.set(b.x, b.y, b.z);
  }

  /** ms of stagger left on MY player_action row; also flashes the HUD once
   *  per server-recorded whiff (compares the row's stamp with the last seen). */
  private myStaggerLeftMs(): number {
    const conn = atlas.conn;
    if (!conn?.identity) return 0;
    const db = conn.db as unknown as { playerAction?: { identity: { find(i: unknown): { staggeredUntil: { microsSinceUnixEpoch: bigint }; lastWhiffAt: { microsSinceUnixEpoch: bigint } } | undefined } } };
    const a = db.playerAction?.identity.find(conn.identity);
    if (!a) return 0;
    const w = Number(a.lastWhiffAt.microsSinceUnixEpoch / 1000n);
    if (w > this.lastWhiffSeen) {
      if (this.lastWhiffSeen > 0) { this.lastWhiffFlash = performance.now() + 900; sfx('sfx_weapon_swing', { gain: 0.35, rate: 0.8, cooldownMs: 300 }); }
      this.lastWhiffSeen = w;
    }
    return Number(a.staggeredUntil.microsSinceUnixEpoch / 1000n) - Date.now();
  }

  /** The telegraph ring under a winding-up beast: one shared geometry +
   *  material, a mesh per active windup, filling from 0.3 → 1 of the reach. */
  private syncRing(b: Beast, t: number, reachM: number): void {
    if (t < 0) {
      if (b.ring) { b.root.remove(b.ring); b.ring = null; }
      return;
    }
    if (!this.ringGeo) this.ringGeo = new THREE.RingGeometry(0.72, 1, 40);
    if (!this.ringMat) this.ringMat = new THREE.MeshBasicMaterial({ color: 0xff7a3c, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide });
    if (this.ringMat) markMrtOverlay(this.ringMat); // the telegraph ring lies on the ground (idempotent)
    if (!b.ring) {
      b.ring = new THREE.Mesh(this.ringGeo, this.ringMat);
      b.ring.rotation.x = -Math.PI / 2;
      b.ring.position.y = 0.06;
      b.ring.renderOrder = 5;
      b.root.add(b.ring);
    }
    const s = reachM * (0.3 + 0.7 * t);
    b.ring.scale.set(s, s, 1);
    this.ringMat.opacity = 0.25 + 0.45 * t;
  }

  private npcStaggerLeftMs(key: string): number {
    const db = atlas.conn?.db as unknown as { npcCombat?: { npcKey: { find(k: string): { staggeredUntil: { microsSinceUnixEpoch: bigint } } | undefined } } } | undefined;
    const r = db?.npcCombat?.npcKey.find(key);
    return r ? Number(r.staggeredUntil.microsSinceUnixEpoch / 1000n) - Date.now() : 0;
  }

  // ── Render: creature meshes ───────────────────────────────────────────────
  private async master(species: string): Promise<THREE.Object3D | null> {
    const loaded = this.masters.get(species);
    if (loaded) return loaded;
    // Single-flight via the PROMISE — the old `null` claim made every caller
    // during the in-flight load return null immediately, so all but the first
    // beast spawned from a den wore the grey fallback capsule for life.
    const inflight = this.masterInflight.get(species);
    if (inflight) return inflight;
    const conn = atlas.conn;
    if (!conn || !this.assets) return null;
    const id = prefabAssetId(conn, `prop:creature_${species}`);
    if (id === null) return null;
    const p = (async () => {
      try {
        const m = await this.assets!.load(id);
        this.masters.set(species, m);
        this.masterIds.set(species, id);
        return m;
      } catch { return null; }
      finally { this.masterInflight.delete(species); }
    })();
    this.masterInflight.set(species, p);
    return p;
  }

  dispose(): void {
    this.flushBeasts();
    this.projectiles?.dispose();
    this.impactLights?.dispose();
    this.impactLights = null;
    this.onBoltArrived = null;
    this.projectiles = null;
  }

  /** Drop every beast + release the species GLB refs (world hop / dispose). */
  private flushBeasts(): void {
    for (const [, b] of this.beasts) {
      this.root?.remove(b.root);
      disposeLater(b.barTex);
      disposeLater(b.bar.material);
    }
    this.beasts.clear();
    // the dissolving bodies share the species geometry: they leave with it
    for (const d of this.dying) { this.root?.remove(d.root); for (const m of d.mats) disposeLater(m); }
    this.dying = [];
    for (const p of this.parts.values()) if (p) { disposeLater(p.geometry); disposeLater(p.material); }
    this.parts.clear();
    this.partsWarmed.clear();
    for (const [species, id] of this.masterIds) { this.assets?.dispose(id); this.masters.delete(species); }
    this.masterIds.clear();
  }

  /** Shared fallback resources — a NEW material/geometry per beast meant a
   *  NEW pipeline per beast; a 13-creature bubble entry built them all in ONE
   *  render (live-profiled 2.9 s monolith, 2026-07-31). */
  private static fallbackGeo: THREE.CapsuleGeometry | null = null;
  private static fallbackMats = new Map<number, THREE.MeshStandardMaterial>();

  private fallbackBeast(species: string): THREE.Object3D {
    const g = new THREE.Group();
    const color = species === 'duneback_brute' ? 0x8a6a3a : species === 'spore_drone' ? 0x4a7a5a : 0x555c66;
    WildsModule.fallbackGeo ??= new THREE.CapsuleGeometry(0.5, 1.4, 4, 8);
    let mat = WildsModule.fallbackMats.get(color);
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({ color, roughness: 0.8 });
      WildsModule.fallbackMats.set(color, mat);
    }
    const body = new THREE.Mesh(WildsModule.fallbackGeo, mat);
    body.rotation.z = Math.PI / 2;
    body.position.y = 0.6;
    g.add(body);
    return g;
  }

  /** THE GAIT: the species' bent body, built once per species from its master (null = keep the rigid clone). */
  private partsFor(species: string, master: THREE.Object3D): WildParts | null {
    if (this.parts.has(species)) return this.parts.get(species) ?? null;
    let p: WildParts | null = null;
    try { p = wildParts(master, species); } catch (e) { Logger.warn('Wilds', `no bent body for ${species}: ${String(e).slice(0, 120)}`); p = null; }
    this.parts.set(species, p);
    return p;
  }

  private beginDissolve(root: THREE.Group): boolean {
    const skip = (why: string): false => { (stage3Stats as unknown as { dissolveSkip?: string }).dissolveSkip = why; return false; };
    if (!activePreset().vfxShaders) return skip('flag');
    const p = new THREE.Vector3();
    root.getWorldPosition(p);
    // Under the FP64 rebase world positions are origin-shifted; compare in the same space.
    const sc = this.app?.world.scene;
    const dist = Math.hypot(p.x - (sc?.position.x ?? 0) - atlas.localX, p.z - (sc?.position.z ?? 0) - atlas.localZ);
    if (dist > 120) return skip(`far:${dist.toFixed(0)}`);
    const mats: THREE.Material[] = [], handles: Array<{ progress: { value: number } }> = [];
    try {
      root.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || (m as unknown as { isSprite?: boolean }).isSprite) return;
        const src = Array.isArray(m.material) ? m.material : [m.material];
        const out = src.map((mm) => {
          const c = (mm as THREE.Material).clone();
          handles.push(applyDissolve(c as never, { progress: 0, edgeWidth: 0.08, edgeColor: [4, 1.3, 0.25], noiseScale: 2.5 }));
          mats.push(c);
          return c;
        });
        m.material = Array.isArray(m.material) ? out : out[0];
        m.castShadow = false;
      });
    } catch (e) { return skip(`threw:${String(e).slice(0, 80)}`); }
    if (handles.length === 0) return skip('nomesh');
    this.dying.push({ root, mats, handles, t: 0 });
    stage3Stats.dissolves++;
    stage3Stats.dissolving = this.dying.length;
    return true;
  }

  private stepDying(dt: number): void {
    if (this.dying.length === 0) return;
    for (let i = this.dying.length - 1; i >= 0; i--) {
      const d = this.dying[i];
      d.t += dt;
      const u = dissolveProgress(d.t, DISSOLVE_S);
      for (const h of d.handles) h.progress.value = u;
      if (d.t >= DISSOLVE_S + 0.05) {
        this.root?.remove(d.root);
        for (const m of d.mats) disposeLater(m);
        this.dying.splice(i, 1);
      }
    }
    stage3Stats.dissolving = this.dying.length;
  }

  /**
   * Interactive water (2026-09-11): a creature wading rings the surface every
   * stride — its root is seated on the ground, so water above the root's
   * height is water it walks through. Cheap: one still-level lookup per beast
   * per frame, a stamp per 0.6 m of travel; nothing when the flag is off.
   */
  /** Creature positions near the viewer (the fauna's threats, 2026-09-11). */
  creaturePositions(): Array<{ x: number; y: number; z: number }> {
    const out: Array<{ x: number; y: number; z: number }> = [];
    for (const [, b] of this.beasts) out.push({ x: b.root.position.x, y: b.root.position.y, z: b.root.position.z });
    return out;
  }

  private stampBeasts(): void {
    for (const [, b] of this.beasts) {
      const p = b.root.position;
      const still = waterStillAt(p.x, p.z);
      if (still === null || still < p.y + 0.1) { b.wadeX = undefined; continue; }
      if (b.wadeX !== undefined && b.wadeZ !== undefined) {
        const d = Math.hypot(p.x - b.wadeX, p.z - b.wadeZ);
        if (d < 0.6) continue;
        const s = creatureStamp(b.footR / 0.45, Math.min(6, d * 10));
        stampWater(p.x, p.z, s.strength, s.radius);
      }
      b.wadeX = p.x; b.wadeZ = p.z;
    }
  }

  private frame(dt: number): void {
    this.stampBeasts();
    this.stepDying(dt);
    pruneTelegraphs(performance.now());
    if (++this.reticleAcc >= 6) { this.reticleAcc = 0; this.updateSoftTarget(); }
    if (this.projectiles) {
      const cam = this.fpCamera();
      this.projectiles.setCamera(cam);
      // The on-screen size floor is measured in DRAWING BUFFER pixels — the
      // resolution the frame is actually rasterised at, which on the cheap
      // tiers is a fraction of the canvas (FSR performance renders 458×240).
      const r = this.app?.renderer as unknown as { native?: { getDrawingBufferSize?: (v: THREE.Vector2) => THREE.Vector2 } } | undefined;
      const size = r?.native?.getDrawingBufferSize?.(this._dbSize);
      if (size && size.y > 0) this.projectiles.setViewport(size.y, (cam as THREE.PerspectiveCamera | null)?.fov ?? 75);
      this.projectiles.update(dt);
    }
    this.impactLights?.update(dt * 1000); // wall-time ms: a flash must not stretch under a hitch
    impactDecalsIfAny()?.update(dt);
    if (!this.root) return;
    if (!this.inCommons()) { this.root.visible = false; return; }
    this.root.visible = true;
    const table = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<CreatureRow> } | undefined>)?.creature;
    if (!table) return;
    const seen = new Set<string>();
    // Spawn budget: entering a bubble with N creatures used to build them ALL
    // this frame — N sprite textures + first-sight pipelines in one render.
    // 2/frame drains a 13-creature den in ~7 frames, invisibly.
    let spawnBudget = 2;
    for (const c of table.iter()) {
      // Distance FIRST: the unfiltered creature table (~1,500 rows) used to
      // pay a BigInt→string per row per frame before the 400 m cull (2026-09-12).
      const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
      if (c.state === 'dead' || d > 400) {
        // A beast we were rendering just DIED (vs merely left the bubble):
        // one collapse thud, louder the closer it fell.
        if (c.state === 'dead' && d < 80 && this.beasts.has(String(c.creatureId))) {
          sfxAt('sfx_creature_death', { x: c.x, y: atlas.localY + 0.6, z: c.z }, { gain: Math.max(0.15, 0.6 - d * 0.006), cooldownMs: 300, emitter: String(c.creatureId) });
        }
        continue; // render bubble
      }
      const key = String(c.creatureId);
      seen.add(key);
      let b = this.beasts.get(key);
      if (!b && spawnBudget <= 0) continue; // next frame's budget picks it up
      if (!b) {
        spawnBudget--;
        const root = new THREE.Group();
        root.add(this.fallbackBeast(c.species));
        void this.master(c.species).then((m) => {
          // The SAME record must still own this root: a beast that left the
          // bubble and re-entered before its GLB resolved got its skin on the
          // old (removed) root and lost its HP bar to it (2026-09-12).
          const cur = this.beasts.get(key);
          if (!m || !cur || cur.root !== root) return;
          const L = SPECIES_LEN[c.species] ?? 2.2;
          // THE GAIT: the species' bent body (normalised to THE FRAME: length 1, feet at y = 0) scaled to its length;
          // the beast's own numbers ride its mesh's userData, read per draw. A body that cannot be built stays rigid.
          const parts = this.partsFor(c.species, m);
          let skin: THREE.Object3D;
          if (parts) {
            const mesh = new THREE.Mesh(parts.geometry, parts.material);
            mesh.name = `wild:${c.species}`;
            mesh.scale.setScalar(L);
            mesh.userData.wildA = cur.wildA;
            mesh.userData.wildB = cur.wildB;
            skin = mesh;
            cur.bar.position.y = (parts.height + parts.hover) * L + 0.7; // over the drawn body (the drone stands 2.7 m tall)
          } else {
            skin = m.clone();
            const box = new THREE.Box3().setFromObject(skin);
            const len = Math.max(0.2, Math.max(box.max.x - box.min.x, box.max.z - box.min.z));
            skin.scale.setScalar(L / len);
            const b2 = new THREE.Box3().setFromObject(skin);
            skin.position.y -= b2.min.y; // feet on the ground
          }
          root.clear();
          root.add(skin);
          root.add(cur.bar);
          // the species' pipeline compiles once, off the frame (R4); later beasts share it
          if (parts && this.app && !this.partsWarmed.has(c.species)) { this.partsWarmed.add(c.species); void warmThenShow(this.app, skin); }
        });
        // HP bar sprite.
        const cv = document.createElement('canvas');
        cv.width = 256; cv.height = 56;
        const ctx = cv.getContext('2d')!;
        const tex = new THREE.CanvasTexture(cv);
        const bar = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true })));
        bar.scale.set(2.6, 0.57, 1);
        bar.position.y = (SPECIES_LEN[c.species] ?? 2.2) * 0.9 + 0.9;
        root.add(bar);
        // Footprint-scaled seat (high-relief audit 2026-08-05): a 0.6 m radius
        // under a 3.8 m brute floated its downhill half on any slope.
        const footR = Math.max(0.6, (SPECIES_LEN[c.species] ?? 2.2) * 0.45);
        const y = terrainSeat(this.seed, c.x, c.z, footR, 0.1).y;
        root.position.set(c.x, y, c.z);
        // THE GAIT: the windup's lean is about the beast's OWN lateral axis (YXZ: yaw last) — under the default XYZ
        // order the pitch turned about the WORLD x axis, a sideways roll for a beast facing east.
        root.rotation.order = 'YXZ';
        this.root.add(root);
        if (this.app) void warmThenShow(this.app, root); // R4
        const spec = wildGaitFor(c.species);
        b = {
          root, bar, barCtx: ctx, barTex: tex, lastHp: -1, lastState: '', targetPos: new THREE.Vector3(c.x, y, c.z), seatX: c.x, seatZ: c.z, footR, ring: null,
          glide: makeGlide(c.x, c.z), gait: makeWildGait(Number(c.creatureId % 100003n)), spec, speciesSpeed: SPECIES[c.species]?.speed ?? 3,
          wildA: new THREE.Vector4(), wildB: new THREE.Vector4(), yaw: 0, kickX: 0, kickZ: 0,
        };
        glideRow(b.glide, c.x, c.z, performance.now() / 1000, b.speciesSpeed);
        this.beasts.set(key, b);
      }
      const nowMs = performance.now();
      // THE GAIT — the glide: a new row sets the pace (its distance over the interval since the last); the body walks
      // the rows at that pace and stops on the newest — the old lerp surged at 2.2× and coasted every 2 s tick.
      if (c.x !== b.seatX || c.z !== b.seatZ) {
        b.seatX = c.x; b.seatZ = c.z;
        glideRow(b.glide, c.x, c.z, nowMs / 1000, b.speciesSpeed);
      }
      // COMBAT WAVE hit-stop: both bodies' visuals hold on the contact frame (the glide, the gait and the kick).
      const gdt = frozen(nowMs) ? 0 : dt;
      const drawnSpeed = stepGlide(b.glide, gdt);
      const kd = Math.exp(-6 * gdt);
      b.kickX *= kd; b.kickZ *= kd;
      b.root.position.x = b.glide.x + b.kickX;
      b.root.position.z = b.glide.z + b.kickZ;
      // The tell: lean back + swell over the windup, snap forward at the
      // snapshot; the ground ring fills toward the blow's reach.
      const wkey = `creature:${c.creatureId}`;
      const w = windups.get(wkey);
      const pose = windupPose(nowMs, w?.start ?? 0, w?.ms ?? 0, lungeAt(wkey, nowMs));
      b.root.rotation.x = pose.pitch;
      b.root.scale.setScalar(pose.scale);
      this.syncRing(b, w ? Math.min(1, (nowMs - w.start) / w.ms) : -1, ATTACK_REACH_M + REACH_PAD_M);
      // Reeling (staggered by a heavy blow): a short wobble, no forward motion.
      const staggered = this.npcStaggerLeftMs(wkey) > 0;
      b.root.rotation.z = staggered ? Math.sin(nowMs * 0.03) * 0.08 : 0;
      // Re-seat Y at the CURRENT XZ (high-relief audit): the straight 3D lerp
      // cut a chord through ridges — a creature crossing a hill walked inside
      // it for the whole glide. The XZ glide stays smooth; Y follows terrain.
      b.root.position.y = terrainSeat(this.seed, b.root.position.x, b.root.position.z, b.footR, 0.1).y;
      // The heading turns at a rate: the way it walks, or — standing in reach — toward its quarry when that is me.
      const me = atlas.conn?.identity;
      let want = b.yaw;
      if (drawnSpeed > 0.15) want = headingOf(b.glide.vx, b.glide.vz);
      else if (me && c.targetKind === 'player' && c.targetPlayer.isEqual(me)) want = headingOf(atlas.localX - b.glide.x, atlas.localZ - b.glide.z);
      b.yaw = turnToward(b.yaw, want, drawnSpeed > 2 ? 4 : 2.5, gdt);
      b.root.rotation.y = b.yaw;
      // The gait: the cycle by speed ÷ stride, the swing walk → run, the neck and the strike (lib/wild_gait.ts); the
      // species' shader reads these two vectors from this beast's mesh at its draw.
      stepWildGait(b.gait, b.spec, {
        speed: drawnSpeed,
        windup: pose.pitch < 0 ? Math.min(1, -pose.pitch / WINDUP_LEAN_RAD) : 0,
        lungeNow: pose.pitch > 0,
        staggered,
      }, gdt);
      b.wildA.set(b.gait.phase, b.gait.phasePrev, b.gait.neck, b.gait.swing);
      b.wildB.set(b.gait.windup, b.gait.lunge, b.gait.run, 0);
      if (c.state !== b.lastState) {
        // '*→aggro': the beast has seen someone — the charge warning, scaled by distance.
        if (c.state === 'aggro' && b.lastState !== '' && d < 120) {
          sfxAt('sfx_creature_aggro', { x: c.x, y: atlas.localY + 0.9, z: c.z }, { gain: Math.max(0.12, 0.5 - d * 0.004), cooldownMs: 400, emitter: String(c.creatureId), caption: 'A creature snarls' });
        }
        b.lastState = c.state;
      }
      if (c.hp !== b.lastHp) {
        // The blow LANDED (any client's strike): a thwack scaled by distance.
        if (c.hp < b.lastHp && d < 60) sfxAt('sfx_creature_hit', { x: c.x, y: atlas.localY + 0.9, z: c.z }, { gain: Math.max(0.15, 0.55 - d * 0.007), cooldownMs: 180, emitter: String(c.creatureId) });
        // COMBAT WAVE feel on a blow DEALT nearby: knock the body back along the
        // blow (a decaying offset over the glide), a beat of hit-stop, a small
        // camera nod — dosed, never scaled up because it is cheap.
        if (c.hp < b.lastHp && b.lastHp >= 0 && d < 12) {
          const heavy = b.lastHp - c.hp >= HEAVY_DMG_MIN;
          const kx = b.root.position.x - atlas.localX, kz = b.root.position.z - atlas.localZ;
          const kl = Math.hypot(kx, kz) || 1;
          // a decaying visual offset over the glide (THE GAIT): the old lerp pulled the shoved body back; the glide
          // never moves for a blow, the kick relaxes at 6/s
          b.kickX += (kx / kl) * KNOCKBACK_M * (heavy ? 1.6 : 1);
          b.kickZ += (kz / kl) * KNOCKBACK_M * (heavy ? 1.6 : 1);
          feel.knockbacks++;
          hitStop(performance.now(), heavy);
          cameraImpulse(KICK_DEALT_PITCH * (heavy ? 1.5 : 1), 0);
          addTrauma(TRAUMA_DEALT);
        }
        b.lastHp = c.hp;
        const spec = SPECIES[c.species];
        const frac = Math.max(0, Math.min(1, c.hp / (spec?.hp ?? 60)));
        const g = b.barCtx;
        const plate = SPECIES_PLATE[c.species] ?? { title: pretty(c.species), sub: 'wild beast' };
        g.clearRect(0, 0, 256, 56);
        g.textAlign = 'center';
        g.font = '600 20px system-ui';
        g.fillStyle = '#eef4ff';
        g.strokeStyle = 'rgba(0,0,0,0.85)';
        g.lineWidth = 4;
        g.strokeText(plate.title, 128, 20);
        g.fillText(plate.title, 128, 20);
        g.font = '13px system-ui';
        g.fillStyle = '#b9c8dd';
        g.strokeText(plate.sub, 128, 36);
        g.fillText(plate.sub, 128, 36);
        g.fillStyle = 'rgba(10,14,22,0.85)';
        g.fillRect(48, 42, 160, 11);
        g.fillStyle = frac > 0.4 ? '#7bc86c' : '#e5534b';
        g.fillRect(50, 44, 156 * frac, 7);
        b.barTex.needsUpdate = true;
      }
    }
    for (const [key, b] of this.beasts) {
      if (!seen.has(key)) {
        if (b.ring) { b.root.remove(b.ring); b.ring = null; }
        b.root.remove(b.bar);
        disposeLater(b.barTex);
        disposeLater(b.bar.material); // one SpriteMaterial leaked per creature before
        this.beasts.delete(key);
        // Stage 3: a creature that leaves the picture near the player DISSOLVES
        // (edge-lit, object-space noise) instead of blinking out — its skin's
        // materials are cloned so the shared species master is untouched.
        if (!this.beginDissolve(b.root)) this.root.remove(b.root);
      }
    }
  }

  // ── The work terminal ─────────────────────────────────────────────────────
  private toggleTerminal(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    if (!this.inCommons() || !this.ensureWorld()) return;
    const site = this.nearestTerminalSite();
    if (!site) return; // terminals stand in cities and towns
    this.winSid = site.id;
    if (!this.win) {
      this.win = makeWindow({
        id: 'work-terminal', title: 'Work Terminal', icon: 'chart',
        width: 470, height: 520, minWidth: 380, minHeight: 320, x: 160, y: 90,
      });
      this.winBody = document.createElement('div');
      this.winBody.style.cssText = 'padding:8px 10px;color:var(--ax-text);font:12px/1.5 var(--ax-font);overflow:auto;height:100%';
      this.win.body.appendChild(this.winBody);
    }
    this.win.setOpen(true);
    this.renderTerminal();
  }

  private renderTerminal(): void {
    const b = this.winBody;
    const conn = atlas.conn;
    if (!b || !conn) return;
    const site = this.sites.find((s) => s.id === this.winSid);
    if (!site) return;
    this.win?.setTitle(`Work Terminal — ${this.names.get(site.id) ?? ''}`);
    const epoch = missionEpoch(Date.now());
    const board = missionsFor(this.seed, site.id, epoch, this.sites);
    const holds = this.myHolds();
    const html: string[] = [];
    if (this.lastError) {
      html.push(`<div style="color:var(--ax-bad);margin-bottom:6px">${esc(this.lastError)}</div>`);
    }
    html.push('<div style="font:700 11px var(--ax-font);letter-spacing:.08em;color:var(--ax-hi);text-transform:uppercase;margin-bottom:4px">postings</div>');
    for (const m of board) {
      const held = holds.find((h) => Number(h.sid) === m.sid && h.slot === m.slot && h.epoch === m.epoch);
      html.push(`<div style="padding:5px 7px;margin-bottom:5px;border:1px solid ${held ? 'var(--ax-accent)' : 'var(--ax-line)'};border-radius:var(--ax-radius)">`);
      if (m.kind === 'cull') {
        html.push(`<b>CULL</b> — ${m.needed} × ${esc(pretty(m.species))} menacing the approaches`);
      } else if (m.kind === 'delivery') {
        html.push(`<b>DELIVERY</b> — parcel to ${esc(this.names.get(m.targetSiteId) ?? `settlement ${m.targetSiteId}`)}`);
      } else {
        html.push(`<b>SURVEY</b> — chart (${Math.round(m.wayX)}, ${Math.round(m.wayZ)})`);
      }
      html.push(`<div style="opacity:.7">${m.rewardSov} SOV · +${m.standing} standing</div>`);
      if (held) {
        const done = held.kind === 'cull' ? held.progress >= held.needed : true;
        html.push(`<div style="margin-top:3px">${held.kind === 'cull' ? `progress ${held.progress}/${held.needed} · ` : ''}` +
          `<button data-complete="${held.missionHoldId}" ${done ? '' : 'disabled'} style="padding:1px 10px">Complete</button> ` +
          `<button data-abandon="${held.missionHoldId}" style="padding:1px 10px">Abandon</button></div>`);
      } else {
        html.push(`<div style="margin-top:3px"><button data-take="${m.slot}" style="padding:1px 10px">Accept</button></div>`);
      }
      html.push('</div>');
    }
    const foreign = holds.filter((h) => Number(h.sid) !== site.id);
    if (foreign.length > 0) {
      html.push('<div style="font:700 11px var(--ax-font);letter-spacing:.08em;color:var(--ax-hi);text-transform:uppercase;margin:8px 0 4px">held elsewhere</div>');
      for (const h of foreign) {
        html.push(`<div style="opacity:.8;padding:2px 0">${esc(h.kind)} at ${esc(this.names.get(Number(h.sid)) ?? String(h.sid))}` +
          `${h.kind === 'cull' ? ` (${h.progress}/${h.needed})` : ''} <button data-abandon="${h.missionHoldId}" style="padding:0 8px;font-size:10px">abandon</button></div>`);
      }
    }
    b.innerHTML = html.join('');
    const go = (p: Promise<void>): void => {
      p.then(() => { this.lastError = ''; this.renderTerminal(); })
        .catch((e: Error) => { this.lastError = String(e.message ?? e); this.renderTerminal(); });
    };
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-take]'))) {
      el.addEventListener('click', () => go(conn.reducers.takeMission({ sid: BigInt(site.id), slot: Number(el.dataset.take) })));
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-complete]'))) {
      el.addEventListener('click', () => go(conn.reducers.completeMission({ missionHoldId: BigInt(el.dataset.complete!) })));
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-abandon]'))) {
      el.addEventListener('click', () => go(conn.reducers.abandonMission({ missionHoldId: BigInt(el.dataset.abandon!) })));
    }
  }

  // ── Chrome ────────────────────────────────────────────────────────────────
  private buildHudChrome(): void {
    this.hud = document.createElement('div');
    this.hud.style.cssText =
      'position:fixed;bottom:120px;right:16px;left:auto;text-align:right;z-index:33;display:none;' +
      'font:12px var(--ax-mono);color:var(--ax-text);text-shadow:0 1px 3px #000';
    document.body.appendChild(this.hud);
    this.prompt = document.createElement('div');
    this.prompt.style.cssText =
      'position:fixed;bottom:194px;left:50%;transform:translateX(-50%);z-index:33;display:none;' +
      'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:6px 14px;' +
      'font:12px var(--ax-font);color:var(--ax-text);text-align:center';
    document.body.appendChild(this.prompt);
    this.flash = document.createElement('div');
    this.flash.style.cssText =
      'position:fixed;inset:0;z-index:32;pointer-events:none;background:radial-gradient(ellipse at center, transparent 55%, color-mix(in srgb, var(--ax-bad) 55%, transparent) 100%);' +
      'opacity:0;transition:opacity .5s';
    document.body.appendChild(this.flash);
  }

  // ── Debug surface ─────────────────────────────────────────────────────────
  private devGated(): boolean {
    try {
      return location.hostname === 'localhost' || location.hostname === '127.0.0.1'
        || localStorage.getItem('atlas:debug') === '1';
    } catch { return false; }
  }

  private installDebug(): void {
    const mod = this;
    (window as unknown as { __wilds: object }).__wilds = {
      state(): object {
        const rows = mod.tbl<CreatureRow>('creature');
        const near = mod.nearestCreature(600);
        const v = mod.myVitals();
        const terminal = mod.nearestTerminalSite();
        return {
          lastError: mod.lastError,
          creatures: {
            total: rows.length,
            alive: rows.filter((c) => c.state !== 'dead').length,
            aggro: rows.filter((c) => c.state === 'aggro').length,
            rendered: mod.beasts.size,
            nearest: near ? { id: String(near.row.creatureId), species: near.row.species, hp: near.row.hp, dist: Math.round(near.d), state: near.row.state } : null,
          },
          myHp: v ? `${v.hp}/${v.maxHp}` : 'no vitals yet',
          holds: mod.myHolds().map((h) => `${h.kind}@${h.sid}${h.kind === 'cull' ? ` ${h.progress}/${h.needed}` : ''}`),
          terminal: terminal ? { site: terminal.id, name: mod.names.get(terminal.id) } : null,
          terminalOpen: mod.win?.isOpen() ?? false,
        };
      },
      spawn(species: string): boolean {
        if (!mod.devGated()) return false;
        void atlas.conn?.reducers.debugSpawnCreature({ x: atlas.localX + 9, z: atlas.localZ + 9, species });
        return true;
      },
      strike(): boolean { if (!mod.devGated()) return false; mod.strikeNearest(); return true; },
      openTerminal(): boolean { if (!mod.devGated()) return false; mod.toggleTerminal(); return true; },
      take(slot: number): void {
        if (!mod.devGated()) return;
        const site = mod.nearestTerminalSite();
        if (site) void atlas.conn?.reducers.takeMission({ sid: BigInt(site.id), slot }).catch((e: Error) => { mod.lastError = String(e.message ?? e); });
      },
    };
  }
}
